import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { ToastaService, ToastData, ToastOptions } from 'ngx-toasta';

@Injectable({
  providedIn: 'root',
})
export class ApNotificationService {
  // Implements service to allow user notifications to be displayed.
  // Currently messages generated are "toast" messages
  // see https://github.com/emonney/ngx-toasta

  // The default ToastaConfig is set in webcommon.module.ts, see defaultToastaConfig.

  // ApNotificationService uses defaultTimeout if no timeout given or timeout given
  // is less that the value of defaultTimeout.
  // Type (success, error, warning, info etc) does not impact default timeout length.
  defaultTimeout = 6000;

  constructor(private toastaService: ToastaService) {
  }

  // Newline's are not supported use <br/> or call the ...WLists methods.

  public success(msg: string, title: string = '', timeout: number | null = null) {
    this.showSuccessNotification(title, msg, timeout);
  }

  public successWList(msgs: string[], title: string = '', timeout: number | null = null) {
    const msg = msgs.join('<br/>');
    this.showSuccessNotification(title, msg, timeout, msgs?.length ?? 0);
  }

  public info(msg: string, title: string = '', timeout: number | null = null) {
    this.showInfoNotification(title, msg, timeout);
  }

  public infoWList(msgs: string[], title: string = '', timeout: number | null = null) {
    const msg = msgs.join('<br/>');
    this.showInfoNotification(title, msg, timeout, msgs?.length ?? 0);
  }

  public warning(msg: string, title: string = '', timeout: number | null = null) {
    this.showWarningNotification(title, msg, timeout);
  }

  public warningWList(msgs: string[], title: string = '', timeout: number | null = null) {
    const msg = msgs.join('<br/>');
    this.showWarningNotification(title, msg, timeout, msgs?.length ?? 0);
  }

  public error(msg: string, title: string = '', timeout: number | null = null) {
    this.showErrorNotification(title, msg, timeout);
  }

  public errorWList(msgs: string[], title: string = '', timeout: number | null = null) {
    const msg = msgs.join('<br/>');
    this.showErrorNotification(title, msg, timeout, msgs?.length ?? 0);
  }

  public displayStandardError(timeout: number | null = null) {
    const title = 'Something went wrong.';
    const msg = 'An unknown error occurred in the operation. Please contact your support representative.';
    this.showErrorNotification(title, msg, timeout);
  }

  private showSuccessNotification(title: string, msg: string, timeout: number | null, msgCount = 0) {
    const toastOptions: ToastOptions = {
      msg,
      onAdd: (toast: ToastData) => this.onAdd(toast),
      onRemove: (toast: ToastData) => this.onRemove(toast),
      showClose: true,
      showDuration: false,
      timeout: this.getTimeout(this.defaultTimeout, timeout, msgCount),
      title: title && title !== '' ? title : 'Success',
    };
    this.toastaService.success(toastOptions);
  }

  private showInfoNotification(title: string, msg: string, timeout: number | null, msgCount = 0) {
    const toastOptions: ToastOptions = {
      msg,
      onAdd: (toast: ToastData) => this.onAdd(toast),
      onRemove: (toast: ToastData) => this.onRemove(toast),
      showClose: true,
      showDuration: false,
      timeout: this.getTimeout(this.defaultTimeout, timeout, msgCount),
      title: title && title !== '' ? title : 'Information',
    };
    this.toastaService.info(toastOptions);
  }

  private showWarningNotification(title: string, msg: string, timeout: number | null, msgCount = 0) {
    const toastOptions: ToastOptions = {
      msg,
      onAdd: (toast: ToastData) => this.onAdd(toast),
      onRemove: (toast: ToastData) => this.onRemove(toast),
      showClose: true,
      showDuration: false,
      timeout: this.getTimeout(this.defaultTimeout, timeout, msgCount),
      title: title && title !== '' ? title : 'Warning',
    };
    this.toastaService.warning(toastOptions);
  }

  private showErrorNotification(title: string, msg: string, timeout: number | null, msgCount = 0) {
    const toastOptions: ToastOptions = {
      msg,
      onAdd: (toast: ToastData) => this.onAdd(toast),
      onRemove: (toast: ToastData) => this.onRemove(toast),
      showClose: true,
      showDuration: false,
      timeout: this.getTimeout(this.defaultTimeout, timeout, msgCount),
      title: title && title !== '' ? title : 'Error',
    };
    this.toastaService.error(toastOptions);
  }

  // Ensure timeout is at least as long as default timout. If multiple messsages in
  // toaster (not sure this is a good practice) we add 3 seconds for each message.
  private getTimeout(defaultTimeout: number, timeout: number | null, msgCount = 0) {
    if (timeout === null) {
      return Math.max(defaultTimeout, defaultTimeout + (msgCount > 1 ? msgCount * 3000 : 0));
    } else {
      return Math.max(defaultTimeout, timeout + (msgCount > 1 ? msgCount * 3000 : 0));
    }
  }
  onAdd(toast: ToastData) {
    console.log(formatDate(Date.now(), 'dd/MM/yyyy hh:mm:ss', 'en-US') + ' Toast ' + toast.id
      + ' type: ' + toast.type + ' timeout: ' + toast.timeout + ' has been added!');
  }

  onRemove(toast: ToastData) {
    console.log(formatDate(Date.now(), 'dd/MM/yyyy hh:mm:ss', 'en-US') + ' Toast ' + toast.id + ' has been removed!');
  }
}
